import { LakehouseTableAclInput, LakehouseTableAclOperationEnum } from '@vendia/management-api-types'
// import { LakehouseTableAclOperationEnum } from '@vendia/management-api-types/dist/generated/graphql/graphql'
import { Schema } from 'jsonschema'
import { ALL_PARTNERS_STAR } from 'src/components/tables/sharing-rules-table.utils'
import { transformObjString } from 'src/pages/data-importer/utils'
import { VendiaUniqueKey } from 'src/types/schema'

import { AccessLevel, Acl, Operation, Row, SharingRule, SharingRules } from './csv-uploader'

const getOperationName = ({ prefix, entity }: { prefix: string; entity: string }): string => {
  return `${prefix}_${entity}`
}

// Generate the graphql input from the row data
const generateGraphqlInputFromRow = ({ row, schema, entity }: { row: Row; schema: Schema; entity: string }): string => {
  const keys = Object.keys(row)
  const values = Object.values(row)
  const graphqlInput = values
    .map((value, index) => {
      const key = keys[index]
      const property = (schema.properties?.[entity]?.items as Schema)?.properties?.[key]
      const schemaType = property?.type

      if (schemaType === 'string') {
        if (property?.enum) {
          return `${key}: ${value}`
        }
        // Escape double quotes in the string value
        const safeValue = (value as string)?.replace(/"/g, '\\"').trim()
        return `${key}: "${safeValue}"`
      }
      if (schemaType === 'number' || schemaType === 'integer') {
        return `${key}: ${value}`
      }
      if (schemaType === 'boolean') {
        return `${key}: ${value}`
      }
      // added the support for nested objects like array and object
      // transform the array and object to GraphQL input compatible string
      // i.e. remove the quotes from object keys
      if (schemaType === 'array') {
        const arrayValue = JSON.parse(value.toString())
        const transformedValue = `[${arrayValue.map((val: any) => transformObjString(JSON.stringify(val))).join(', ')}]`
        return `${key}: ${transformedValue}`
      }
      if (schemaType === 'object') {
        const transformedValue = transformObjString(value.toString())
        return `${key}: ${transformedValue}`
      }
      throw new Error(`${schemaType} not supported`)
    })
    .join(', ')
  return graphqlInput
}

// Generate graphql result from the schema
const generateGraphqlResultFromSchema = ({ schema, entity }: { schema: Schema; entity: string }): string => {
  const properties = (schema.properties?.[entity]?.items as Schema)?.properties
  if (!properties) return ''
  const propertyKeys = Object.keys(properties)

  // iterate over the object properties and skip the object and array types in the result
  // this is to avoid the complexity of nested objects and arrays
  // this will not return the nested objects and arrays in the result
  const resultKeys = propertyKeys
    .map((key) => {
      if (properties?.[key]?.type !== 'object' && properties?.[key]?.type !== 'array') {
        return key
      }
      return undefined
    })
    .filter(Boolean)
    .join('\n')

  return `
    ... on Self_${entity} {
      _id
      _owner
      _acl {
        operations
        path
        principal {
          nodes
        }
      }
      ${resultKeys}
    }
    `
}

const generateGraphqlKeyParamaterFromSchema = ({
  schema,
  entity,
  row,
}: {
  schema: Schema
  entity: string
  row: Row
}): string => {
  const keys = ((schema.properties?.[entity]?.items as any)?.[VendiaUniqueKey] as string[]) || []
  const firstKey = keys[0]
  const firstKeyValue = row[firstKey]

  return `${firstKey}: "${firstKeyValue}"`
}

const mapOperationToAccessLevel = (operation: Operation): AccessLevel => {
  // Grouping these operations together for simplicity
  if (['GET', 'LIST', 'READ'].includes(operation)) {
    return 'READ'
    // Grouping these operations together for simplicity
  } else if (['UPDATE', 'INVOKE', 'DELETE', 'WRITE'].includes(operation)) {
    return 'WRITE'
  } else if (['ALL'].includes(operation)) {
    return 'READ_WRITE'
  } else if (['UPDATE_ACL'].includes(operation)) {
    return 'ADMIN'
  }
  return 'NO_ACCESS' as any
}

export const generateSharingRulesFromAcl = ({ acl }: { acl: Acl[] }): SharingRule[] => {
  // Find all the unique partners in the acl
  const uniquePartnersInAcls = acl.reduce((acc: string[], curr) => {
    const partners = curr.principal.nodes
    partners.forEach((partner) => {
      if (!acc.includes(partner)) {
        acc.push(partner)
      }
    })
    return acc
  }, [])

  const sharingRules: SharingRule[] = uniquePartnersInAcls.map((partner) => {
    const fields = acl
      .filter((acl) => {
        return acl.principal.nodes.includes(partner)
      })
      .map((acl) => {
        return {
          name: acl.path || '*',
          accessLevel: mapOperationToAccessLevel(acl.operations[0]),
        }
      })

    return {
      partner,
      fields,
    }
  })

  return sharingRules
}

export const generateAclsFromSharingRules = ({ sharingRules }: { sharingRules?: SharingRule[] }): Acl[] => {
  if (!sharingRules) {
    return []
  }
  const rulesWithSomeAccess = sharingRules.filter((rule) => {
    return rule.fields.some((field) => field.accessLevel !== 'NO_ACCESS')
  })

  // @ts-ignore Backend requires path as string or not set at all (i.e. undefined)
  const acls: Acl[] = rulesWithSomeAccess
    .map((rule) => {
      return rule.fields
        .filter((field) => field.accessLevel !== 'NO_ACCESS') // Default is no access, so filter out these fields
        .map((field) => {
          return {
            operations: [field.accessLevel === 'READ_WRITE' ? 'ALL' : (field.accessLevel as Operation)],
            path: field.name === '*' ? undefined : `${field.name}`,
            principal: {
              nodes: [rule.partner],
            },
          }
        })
    })
    .flat()
  return acls
}

export const generateAclInputFromSharingRules = ({ sharingRules }: { sharingRules?: SharingRules }): string => {
  if (!sharingRules) {
    return ''
  }
  const { rules } = sharingRules

  const acls = generateAclsFromSharingRules({ sharingRules: rules })

  const aclsStrings = acls.map((acl) => {
    return `
      {
        principal: {
          nodes: [${acl.principal.nodes.map((node) => `"${node}"`).join(', ')}]
        },
        operations: [${acl.operations.map((operation) => `${operation}`).join(', ')}],
        path: ${acl.path ? `"${acl.path}"` : `""`}
      }`
  })
  return `{
      acl: [${aclsStrings.join(', \n')}]
    }`
}

export const generateAclInputFromAcl = ({ acl }: { acl?: Acl[] }): string | null => {
  if (!acl) {
    return null
  }
  const aclsStrings = acl.map((acl) => {
    return `
      {
        principal: {
          nodes: [${acl.principal.nodes.map((node) => `"${node}"`).join(', ')}]
        },
        operations: [${acl.operations.map((operation) => `${operation}`).join(', ')}],
        ${acl.path ? `path: "${acl.path}"` : ``}
      }`
  })
  return `{
      acl: [${aclsStrings.join(', \n')}]
    }`
}

export const generateUpdateOperation = ({
  entity,
  row,
  schema,
  aclInput,
}: {
  entity: string
  row: Row
  schema: Schema
  aclInput?: string
}): string => {
  const keyParam = generateGraphqlKeyParamaterFromSchema({
    schema,
    entity,
    row,
  })

  return `${getOperationName({
    prefix: 'update',
    entity,
  })}(${keyParam}, ${aclInput ? `aclInput: ${aclInput},` : ``} input: {${generateGraphqlInputFromRow({
    entity,
    row,
    schema,
  })}}, syncMode: NODE_COMMITTED) {
        transaction {
          _id
          _owner
          transactionId
          submissionTime
          version
        }
        result {
          ${generateGraphqlResultFromSchema({ entity, schema })}
        }
      }
    `
}

export const generateAddOperation = ({
  entity,
  row,
  schema,
  aclInput,
}: {
  entity: string
  row: Row
  schema: Schema
  aclInput?: string
}): string => {
  return `${getOperationName({ prefix: 'add', entity })}(${
    aclInput ? `aclInput: ${aclInput},` : ``
  } input: {${generateGraphqlInputFromRow({
    entity,
    row,
    schema,
  })}}, syncMode: NODE_COMMITTED) {
        transaction {
          _id
          _owner
          transactionId
          submissionTime
          version
        }
        result {
          ${generateGraphqlResultFromSchema({ entity, schema })}
        }
      }
    `
}

export const generateTableAclInputFromAccessLevel = (
  accessLevel: Record<string, AccessLevel>,
): LakehouseTableAclInput[] => {
  const tableAcls: LakehouseTableAclInput[] = Object.entries(accessLevel)
    .filter(([_, accessLevel]) => accessLevel !== 'NO_ACCESS') // Default is no access, so filter out these fields
    .map(([partner, accessLevel]) => {
      return {
        principal: {
          nodes: [partner === ALL_PARTNERS_STAR ? '*' : partner],
        },
        operations: [
          accessLevel === 'READ_WRITE' ? LakehouseTableAclOperationEnum.All : LakehouseTableAclOperationEnum.Read,
        ],
      }
    })

  // NOTE: Removing this and returning tableAcls above will revert back to the typical ACL format
  // with separate top-level array entries for each node. For now, back-end is restriced to an empty
  // array (private) or a _single_ entry with wildcard or all nodes in one principal.nodes array
  const hasWildcard = tableAcls.some((acl) => acl.principal.nodes.includes('*'))
  const emptyOrSingleAcl =
    tableAcls.length === 0
      ? []
      : [
          // Single ACL entry
          {
            principal: {
              // All nodes in this one array OR wildcard
              nodes: hasWildcard ? ['*'] : tableAcls.flatMap((acl) => acl.principal.nodes),
            },
            operations: [LakehouseTableAclOperationEnum.Read], // Only READ is allowed for now
          },
        ]

  return emptyOrSingleAcl
}
